import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Loader2, 
  Upload, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  XCircle, 
  Users,
  Calendar,
  MapPin
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BulkPaxImportPage() {
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [results, setResults] = useState(null);
  
  // Form state - array of PAX entries
  const [entries, setEntries] = useState([
    { location_code: '', date: new Date().toISOString().split('T')[0], pax: '' }
  ]);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const addEntry = () => {
    setEntries([
      ...entries,
      { location_code: '', date: new Date().toISOString().split('T')[0], pax: '' }
    ]);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      setEntries(entries.filter((_, i) => i !== index));
    }
  };

  const updateEntry = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate entries
    const validEntries = entries.filter(e => 
      e.location_code && e.date && e.pax && parseInt(e.pax) > 0
    );
    
    if (validEntries.length === 0) {
      toast.error('Please fill in at least one complete entry');
      return;
    }
    
    setImporting(true);
    setResults(null);
    
    try {
      const payload = {
        items: validEntries.map(e => ({
          location_code: e.location_code.toUpperCase(),
          date: e.date,
          pax: parseInt(e.pax)
        }))
      };
      
      const res = await axios.post(`${API}/import/pax`, payload);
      setResults(res.data);
      
      if (res.data.successful.length > 0) {
        toast.success(`Successfully imported PAX for ${res.data.successful.length} entries`);
      }
      if (res.data.failed.length > 0) {
        toast.warning(`${res.data.failed.length} entries failed to import`);
      }
      
      // Clear successful entries from form
      if (res.data.successful.length === validEntries.length) {
        setEntries([{ location_code: '', date: new Date().toISOString().split('T')[0], pax: '' }]);
      }
      
    } catch (error) {
      console.error('Failed to import PAX:', error);
      toast.error('Failed to import PAX data');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="bulk-pax-import-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
            Bulk PAX Import
          </h1>
          <p className="text-slate-600 mt-1">
            Import PAX data for multiple locations at once
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Import Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Upload className="w-5 h-5 text-violet-600" />
                  PAX Data Entry
                </CardTitle>
                <CardDescription>
                  Enter PAX values for each location and date
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Entry rows */}
                  <div className="space-y-3">
                    {entries.map((entry, index) => (
                      <div 
                        key={index} 
                        className="flex flex-col sm:flex-row gap-3 p-4 rounded-lg bg-slate-50 border border-slate-100"
                        data-testid={`pax-entry-row-${index}`}
                      >
                        {/* Location Code */}
                        <div className="flex-1">
                          <Label className="text-xs text-slate-500 mb-1 block">Location Code</Label>
                          <select
                            value={entry.location_code}
                            onChange={(e) => updateEntry(index, 'location_code', e.target.value)}
                            className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                            data-testid={`location-select-${index}`}
                          >
                            <option value="">Select location...</option>
                            {locations.map(loc => (
                              <option key={loc.id} value={loc.code}>
                                {loc.code} - {loc.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Date */}
                        <div className="w-full sm:w-40">
                          <Label className="text-xs text-slate-500 mb-1 block">Date</Label>
                          <Input
                            type="date"
                            value={entry.date}
                            onChange={(e) => updateEntry(index, 'date', e.target.value)}
                            className="h-10"
                            data-testid={`date-input-${index}`}
                          />
                        </div>
                        
                        {/* PAX */}
                        <div className="w-full sm:w-32">
                          <Label className="text-xs text-slate-500 mb-1 block">PAX Count</Label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="e.g. 1500"
                            value={entry.pax}
                            onChange={(e) => updateEntry(index, 'pax', e.target.value)}
                            className="h-10"
                            data-testid={`pax-input-${index}`}
                          />
                        </div>
                        
                        {/* Remove button */}
                        <div className="flex items-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeEntry(index)}
                            disabled={entries.length === 1}
                            className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                            data-testid={`remove-entry-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add more button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addEntry}
                    className="w-full border-dashed"
                    data-testid="add-entry-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Entry
                  </Button>
                  
                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={importing}
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    data-testid="import-pax-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import PAX Data
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Results & Info */}
          <div className="space-y-6">
            {/* Location Reference */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-violet-600" />
                  Location Codes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {locations.map(loc => (
                    <div key={loc.id} className="flex justify-between text-sm">
                      <span className="font-mono font-semibold text-violet-600">{loc.code}</span>
                      <span className="text-slate-500 truncate ml-2">{loc.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Import Results */}
            {results && (
              <Card data-testid="import-results">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    {results.successful.length > 0 ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                    Import Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Summary */}
                  <div className="flex gap-2">
                    <Badge className="bg-emerald-500">
                      {results.successful.length} Success
                    </Badge>
                    {results.failed.length > 0 && (
                      <Badge className="bg-red-500">
                        {results.failed.length} Failed
                      </Badge>
                    )}
                  </div>
                  
                  {/* Successful */}
                  {results.successful.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500">Successfully Updated:</p>
                      {results.successful.map((item, i) => (
                        <div key={i} className="text-sm p-2 rounded bg-emerald-50 text-emerald-700">
                          <span className="font-mono">{item.location_code}</span> · {item.date} · PAX: {item.pax.toLocaleString()}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Failed */}
                  {results.failed.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500">Failed:</p>
                      {results.failed.map((item, i) => (
                        <div key={i} className="text-sm p-2 rounded bg-red-50 text-red-700">
                          <span className="font-mono">{item.location_code}</span> · {item.date}
                          <p className="text-xs mt-1 opacity-75">{item.reason}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Help Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3 text-sm text-slate-600">
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 mt-0.5 text-violet-500" />
                    <p>PAX (attendance) data updates preliminary scorecards to final status</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 mt-0.5 text-violet-500" />
                    <p>Match dates with existing daily entries for successful import</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
